import { supabase } from "@/integrations/supabase/client";
import { mirrorAuthIdentity } from "@/lib/mirrorAuthSession";
import { cloudTaskId, taskUnsafeControl } from "../../supabase/functions/_shared/peopleTasksDomain";

export interface MicrosoftTodoImportConnection { id: string; generation: string }
export interface MicrosoftTodoImportList { id: string; title: string }
export interface MicrosoftTodoImportScope {
  ownerId: string;
  authSessionId: string;
  signal?: AbortSignal;
  /** A monotonic UI lease; returning to the same list or actor cannot revive it. */
  isCurrent?: () => boolean;
}
interface Page {
  connection: MicrosoftTodoImportConnection;
  scanned: number;
  next_cursor: string | null;
  has_more: boolean;
  remote_write: false;
}
export interface MicrosoftTodoImportLists extends Page {
  ok: true;
  provider: "microsoft_todo";
  phase: "lists";
  coverage: "task_lists";
  lists: MicrosoftTodoImportList[];
}
interface PreparedPage extends Page {
  ok: true;
  provider: "microsoft_todo";
  phase: "prepare";
  coverage: "selected_list_page";
  list: MicrosoftTodoImportList;
  batch_id: string;
  expires_at: string;
}
export interface MicrosoftTodoImportResult {
  status: "applied" | "conflict";
  inserted: number;
  updated: number;
  unchanged: number;
  conflict: number;
  tombstone: number;
  pending: number;
}
export interface MicrosoftTodoImportApplied extends Page {
  ok: true;
  provider: "microsoft_todo";
  phase: "apply";
  coverage: "selected_list_page";
  list: MicrosoftTodoImportList;
  batch_id: string;
  result: MicrosoftTodoImportResult;
  replayed: boolean;
}
export type MicrosoftTodoImportErrorCode =
  | "invalid_session" | "import_cancelled" | "invalid_import_response"
  | "connection_changed" | "list_changed" | "import_expired" | "import_failed";
export class MicrosoftTodoImportError extends Error {
  constructor(public readonly code: MicrosoftTodoImportErrorCode) { super(code); }
}
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const bounded = (v: unknown, max: number): v is string =>
  typeof v === "string" && v.length > 0 && v.length <= max && !taskUnsafeControl(v);
const count = (v: unknown): v is number => Number.isSafeInteger(v) && (v as number) >= 0 && (v as number) <= 20;
const connection = (v: unknown): v is MicrosoftTodoImportConnection =>
  record(v) && typeof v.id === "string" && uuid.test(v.id) && typeof v.generation === "string" && uuid.test(v.generation);
function list(v: unknown): v is MicrosoftTodoImportList {
  if (!record(v) || !bounded(v.title, 200) || !v.title.trim()) return false;
  try { cloudTaskId(v.id); return true; } catch { return false; }
}
const cursor = (v: unknown): v is string | null => v === null || bounded(v, 16384);
const sameConnection = (a: MicrosoftTodoImportConnection, b: MicrosoftTodoImportConnection) =>
  a.id === b.id && a.generation === b.generation;
const sameList = (a: MicrosoftTodoImportList, b: MicrosoftTodoImportList) => a.id === b.id && a.title === b.title;
const invalid = (): never => { throw new MicrosoftTodoImportError("invalid_import_response"); };
function page(v: unknown, phase: "lists" | "prepare" | "apply"): v is Page & Record<string, unknown> {
  return record(v) && v.ok === true && v.provider === "microsoft_todo" && v.phase === phase &&
    v.remote_write === false && connection(v.connection) && count(v.scanned) && cursor(v.next_cursor) &&
    typeof v.has_more === "boolean" && v.has_more === (v.next_cursor !== null);
}
function parseLists(v: unknown): MicrosoftTodoImportLists {
  if (!page(v, "lists") || v.coverage !== "task_lists" || !Array.isArray(v.lists) ||
    v.lists.length > 20 || v.lists.length !== v.scanned || !v.lists.every(list) ||
    new Set(v.lists.map(l => l.id)).size !== v.lists.length) return invalid();
  return v as unknown as MicrosoftTodoImportLists;
}
function parsePrepared(v: unknown): PreparedPage {
  if (!page(v, "prepare") || v.coverage !== "selected_list_page" || !list(v.list) ||
    typeof v.batch_id !== "string" || !uuid.test(v.batch_id) || typeof v.expires_at !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/u.test(v.expires_at) ||
    !Number.isFinite(Date.parse(v.expires_at)) ||
    new Date(v.expires_at).toISOString().slice(0, 19) !== v.expires_at.slice(0, 19)) return invalid();
  return v as unknown as PreparedPage;
}
function parseApplied(v: unknown): MicrosoftTodoImportApplied {
  if (!page(v, "apply") || v.coverage !== "selected_list_page" || !list(v.list) ||
    typeof v.batch_id !== "string" || !uuid.test(v.batch_id) || typeof v.replayed !== "boolean" || !record(v.result)) return invalid();
  const r = v.result;
  const keys = ["inserted", "updated", "unchanged", "conflict", "tombstone", "pending"] as const;
  if (!["applied", "conflict"].includes(String(r.status)) || !keys.every(k => count(r[k])) ||
    keys.reduce((total, k) => total + (r[k] as number), 0) !== v.scanned ||
    r.status === "conflict" && (r.conflict !== v.scanned || keys.some(k => k !== "conflict" && r[k] !== 0))) return invalid();
  return v as unknown as MicrosoftTodoImportApplied;
}

/** Transport abort only revokes future stages. An apply already admitted by the server may finish locally. */
async function withScope<T>(scope: MicrosoftTodoImportScope, work: (invoke: (body: Record<string, unknown>) => Promise<unknown>) => Promise<T>): Promise<T> {
  if (!uuid.test(scope.ownerId) || !uuid.test(scope.authSessionId)) throw new MicrosoftTodoImportError("invalid_session");
  const controller = new AbortController();
  let revoked = false;
  const abort = () => controller.abort();
  scope.signal?.addEventListener("abort", abort, { once: true });
  if (scope.signal?.aborted) abort();
  const assertLease = () => {
    if (revoked) throw new MicrosoftTodoImportError("invalid_session");
    if (controller.signal.aborted || scope.isCurrent?.() === false) throw new MicrosoftTodoImportError("import_cancelled");
  };
  const matches = (session: unknown) => {
    const identity = mirrorAuthIdentity(session);
    return identity?.ownerId === scope.ownerId && identity.authSessionId === scope.authSessionId;
  };
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    // Permanently revoke this operation, including owner/SID A -> B -> A.
    if (!matches(session)) { revoked = true; abort(); }
  });
  const token = async () => {
    assertLease();
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    assertLease();
    if (userError || user?.id !== scope.ownerId) throw new MicrosoftTodoImportError("invalid_session");
    const { data: { session }, error } = await supabase.auth.getSession();
    assertLease();
    const identity = mirrorAuthIdentity(session);
    if (error || !matches(session) || !identity) throw new MicrosoftTodoImportError("invalid_session");
    return identity.accessToken;
  };
  try {
    const invoke = async (body: Record<string, unknown>) => {
      const bearer = await token();
      assertLease();
      const requestId = crypto.randomUUID();
      const { data, error } = await supabase.functions.invoke("microsoft-todo-import", {
        body: { ...body, owner_id: scope.ownerId, auth_session_id: scope.authSessionId, request_id: requestId },
        headers: { Authorization: "Bearer " + bearer },
        signal: controller.signal,
      });
      await token();
      if (error || record(data) && data.ok !== true) throw new MicrosoftTodoImportError("import_failed");
      if (body.phase === "prepare" && (!record(data) || data.batch_id !== requestId)) return invalid();
      return data;
    };
    return await work(invoke);
  } catch (error) {
    assertLease();
    if (error instanceof MicrosoftTodoImportError) throw error;
    throw new MicrosoftTodoImportError("import_failed");
  } finally {
    subscription.unsubscribe();
    scope.signal?.removeEventListener("abort", abort);
  }
}

export async function loadMicrosoftTodoImportLists(
  scope: MicrosoftTodoImportScope,
  options: { cursor?: string; connection?: MicrosoftTodoImportConnection } = {},
): Promise<MicrosoftTodoImportLists> {
  if (options.cursor !== undefined && !bounded(options.cursor, 16384) ||
    options.connection !== undefined && !connection(options.connection) ||
    options.cursor !== undefined && !options.connection) return invalid();
  return withScope(scope, async invoke => {
    const response = parseLists(await invoke({ phase: "lists", ...(options.cursor ? { cursor: options.cursor } : {}) }));
    if (options.connection && !sameConnection(options.connection, response.connection)) throw new MicrosoftTodoImportError("connection_changed");
    return response;
  });
}

export async function importMicrosoftTodoListPage(
  scope: MicrosoftTodoImportScope,
  options: { list: MicrosoftTodoImportList; connection: MicrosoftTodoImportConnection; cursor?: string },
): Promise<MicrosoftTodoImportApplied> {
  if (!list(options.list) || !connection(options.connection) ||
    options.cursor !== undefined && !bounded(options.cursor, 16384)) return invalid();
  return withScope(scope, async invoke => {
    const prepared = parsePrepared(await invoke({ phase: "prepare", list_id: options.list.id,
      ...(options.cursor ? { cursor: options.cursor } : {}) }));
    if (!sameConnection(options.connection, prepared.connection)) throw new MicrosoftTodoImportError("connection_changed");
    if (!sameList(options.list, prepared.list)) throw new MicrosoftTodoImportError("list_changed");
    if (Date.parse(prepared.expires_at) <= Date.now()) throw new MicrosoftTodoImportError("import_expired");
    // invoke revalidates the monotonic lease and authenticated actor immediately before apply.
    const applied = parseApplied(await invoke({ phase: "apply", batch_id: prepared.batch_id }));
    if (!sameConnection(prepared.connection, applied.connection)) throw new MicrosoftTodoImportError("connection_changed");
    if (!sameList(prepared.list, applied.list)) throw new MicrosoftTodoImportError("list_changed");
    if (prepared.batch_id !== applied.batch_id || prepared.scanned !== applied.scanned ||
      prepared.next_cursor !== applied.next_cursor || prepared.has_more !== applied.has_more) return invalid();
    return applied;
  });
}
